// ===========================================
// SERVICIO: Notificaciones In-App (CORE)
// ===========================================
/**
 * Servicio de gestión de notificaciones in-app.
 * Requiere cliente autenticado de Supabase como primer parámetro.
 */

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import type {
  Notificacion,
  NotificacionInsert,
  FiltrosNotificaciones,
  TipoNotificacion,
  ResumenNotificaciones,
  DestinatarioSpec,
  NotificacionOrigen,
} from '../types/notificaciones'
import { destinatariosResolver } from './destinatariosResolver'
import { calcularPermisosEfectivos, ROL_MULTITENANT_TO_LEGACY, type Permisos, type RolMultiTenant } from '../../types/roles'

/**
 * Valor de `origen.tabla_fuente` que marca una novedad publicada por un admin.
 * Separa esas filas de las demás notificaciones `sistema` que generan los módulos y los crones.
 */
export const NOVEDAD_SISTEMA_FUENTE = 'novedades_sistema'

export const notificacionesService = {
  // ===========================================
  // CRUD de Notificaciones
  // ===========================================

  /**
   * Lista notificaciones de un usuario
   */
  async listar(client: SupabaseClient<Database>,
    userEmail: string,
    filtros?: FiltrosNotificaciones
  ): Promise<Notificacion[]> {

    const limite = filtros?.limite || 50

    let query = client
      .from('notificaciones')
      .select('*')
      .eq('user_email', userEmail)
      .order('created_at', { ascending: false })
      .limit(limite)

    if (filtros?.tipo) {
      query = query.eq('tipo', filtros.tipo)
    }

    if (filtros?.leida !== undefined) {
      query = query.eq('leida', filtros.leida)
    }

    const { data, error } = await query

    if (error) throw new Error(`Error al listar notificaciones: ${error.message}`)
    return data as Notificacion[]
  },

  /**
   * Obtiene notificaciones no leidas
   */
  async obtenerNoLeidas(client: SupabaseClient<Database>, userEmail: string, limite = 20): Promise<Notificacion[]> {
    return this.listar(client, userEmail, { leida: false, limite })
  },

  /**
   * Obtiene una notificacion por id.
   *
   * ORR-252: el canal de Realtime es publico y solo transporta el id, asi que el
   * contenido se trae por aca — PostgREST evalua RLS con la sesion de Auth0.
   * maybeSingle y no single: una fila no visible para este usuario es null, no una excepcion.
   */
  async obtenerPorId(client: SupabaseClient<Database>, id: string): Promise<Notificacion | null> {

    const { data, error } = await client
      .from('notificaciones')
      .select('*')
      .eq('id', id)
      .maybeSingle()

    if (error) throw new Error(`Error al obtener notificacion: ${error.message}`)
    return data as Notificacion | null
  },

  /**
   * Crea una nueva notificacion
   */
  async crear(client: SupabaseClient<Database>, notificacion: NotificacionInsert): Promise<Notificacion> {

    const insertData = {
      user_email: notificacion.user_email,
      tipo: notificacion.tipo,
      titulo: notificacion.titulo,
      mensaje: notificacion.mensaje,
      enlace: notificacion.enlace || null,
      leida: false,
    }

    const { data, error } = await client
      .from('notificaciones')
      .insert(insertData as never)
      .select()
      .single()

    if (error) throw new Error(`Error al crear notificacion: ${error.message}`)
    return data as Notificacion
  },

  /**
   * Crea notificaciones para multiples usuarios.
   *
   * `origen.organizacion_id` es la organización donde el trigger de ORR-613 evalúa el permiso de
   * los avisos con importes. Sin ella, el trigger usa la única organización activa del caller.
   */
  async crearParaMultiples(client: SupabaseClient<Database>,
    userEmails: string[],
    notificacion: Omit<NotificacionInsert, 'user_email'>,
    origen?: { canal: NotificacionOrigen['canal']; organizacion_id: string }
  ): Promise<number> {

    const notificaciones = userEmails.map((email) => ({
      user_email: email,
      tipo: notificacion.tipo,
      titulo: notificacion.titulo,
      mensaje: notificacion.mensaje,
      enlace: notificacion.enlace || null,
      leida: false,
      ...(origen && { origen }),
    }))

    const { error } = await client
      .from('notificaciones')
      .insert(notificaciones as never)

    if (error) throw new Error(`Error al crear notificaciones: ${error.message}`)
    return userEmails.length
  },

  /**
   * Marca una notificacion como leida
   */
  async marcarLeida(client: SupabaseClient<Database>, id: string): Promise<void> {

    const { error } = await client
      .from('notificaciones')
      .update({ leida: true } as never)
      .eq('id', id)

    if (error) throw new Error(`Error al marcar como leida: ${error.message}`)
  },

  /**
   * Marca todas las notificaciones como leidas
   */
  async marcarTodasLeidas(client: SupabaseClient<Database>, userEmail: string): Promise<number> {

    const { data: updated, error } = await client
      .from('notificaciones')
      .update({ leida: true } as never)
      .eq('user_email', userEmail)
      .eq('leida', false)
      .select()

    if (error) throw new Error(`Error al marcar todas como leidas: ${error.message}`)
    return (updated as unknown[] || []).length
  },

  /**
   * Elimina una notificacion
   */
  async eliminar(client: SupabaseClient<Database>, id: string): Promise<void> {

    const { error } = await client
      .from('notificaciones')
      .delete()
      .eq('id', id)

    if (error) throw new Error(`Error al eliminar notificacion: ${error.message}`)
  },

  /**
   * Elimina notificaciones antiguas (mas de 30 dias)
   */
  async limpiarAntiguas(client: SupabaseClient<Database>, userEmail: string, dias = 30): Promise<number> {

    const fechaLimite = new Date()
    fechaLimite.setDate(fechaLimite.getDate() - dias)

    const { data, error } = await client
      .from('notificaciones')
      .delete()
      .eq('user_email', userEmail)
      .lt('created_at', fechaLimite.toISOString())
      .select()

    if (error) throw new Error(`Error al limpiar notificaciones: ${error.message}`)
    return data?.length || 0
  },

  // ===========================================
  // Helpers de destinatarios (ORR-56)
  // ===========================================

  /**
   * Obtiene los emails de todos los usuarios activos de una organización.
   * Útil para eventos que deben llegar a todo el equipo (ej: nuevo pago registrado).
   *
   * @param excludeEmail — email a excluir (típicamente el actor que disparó el evento)
   * @param rolFilter — opcional: filtrar por roles específicos (ej: solo owner/admin)
   * @param permiso — opcional: solo quien lo tiene efectivo (base → org-rol → extra → denegados).
   *   Si no se pueden leer los overrides de rol, no devuelve a nadie.
   */
  async obtenerDestinatariosOrg(
    client: SupabaseClient<Database>,
    organizacionId: string,
    excludeEmail?: string,
    rolFilter?: ('owner' | 'admin' | 'profesional' | 'asistente' | 'solo_lectura')[],
    permiso?: keyof Permisos
  ): Promise<string[]> {
    // ORR-69 security fix #2: tenant guard.
    // Si el caller pasa un excludeEmail (el actor que dispara la notif),
    // verificamos que ese actor es miembro activo de la org antes de
    // resolver destinatarios. Sin este gate, un user malicioso podria
    // resolver destinatarios de OTRA org pasando org:<uuid-otra>.
    if (excludeEmail) {
      const { data: membership } = await client
        .from('usuario_organizaciones')
        .select('id, usuarios_brainhub!inner(email)')
        .eq('organizacion_id', organizacionId)
        .eq('activo', true)
        .eq('usuarios_brainhub.email', excludeEmail)
        .maybeSingle()

      if (!membership) {
        console.warn(
          `[notificaciones] tenant guard: actor "${excludeEmail}" no es miembro activo de org "${organizacionId}"`,
        )
        return []
      }
    }

    let query = client
      .from('usuario_organizaciones')
      .select('rol, permisos_extra, permisos_denegados, usuarios_brainhub!inner(email, activo)')
      .eq('organizacion_id', organizacionId)
      .eq('activo', true)

    if (rolFilter && rolFilter.length > 0) {
      query = query.in('rol', rolFilter)
    }

    const { data, error } = await query
    if (error) {
      console.error('[notificaciones] error obteniendo destinatarios org:', error)
      return []
    }

    type Row = {
      rol: string
      permisos_extra: Partial<Permisos> | null
      permisos_denegados: (keyof Permisos)[] | null
      usuarios_brainhub: { email: string | null; activo: boolean } | { email: string | null; activo: boolean }[] | null
    }
    let rows = (data ?? []) as unknown as Row[]

    if (permiso) {
      // Misma fuente que usePermissions para la capa org-rol.
      const { data: overrides, error: overridesError } = await client
        .from('rbac_org_rol_override')
        .select('rol, flag, permitido')
        .eq('organizacion_id', organizacionId)
      if (overridesError) {
        console.error('[notificaciones] error leyendo overrides de rol, sin destinatarios:', overridesError)
        return []
      }
      rows = rows.filter((row) => {
        const rolLegacy = ROL_MULTITENANT_TO_LEGACY[row.rol as RolMultiTenant]
        if (!rolLegacy) return false
        const overridesDelRol = Object.fromEntries(
          (overrides ?? []).filter((o) => o.rol === rolLegacy).map((o) => [o.flag, o.permitido]),
        ) as Partial<Record<keyof Permisos, boolean>>
        const efectivos = calcularPermisosEfectivos(row.rol as RolMultiTenant, row.permisos_extra, row.permisos_denegados, overridesDelRol)
        return efectivos[permiso] === true
      })
    }

    const emails = new Set<string>()
    for (const row of rows) {
      const ub = row.usuarios_brainhub
      if (!ub) continue
      const items = Array.isArray(ub) ? ub : [ub]
      for (const item of items) {
        if (item?.activo && item.email && item.email !== excludeEmail) {
          emails.add(item.email)
        }
      }
    }
    return Array.from(emails)
  },

  /**
   * Obtiene el nombre displayable de un cliente via JOIN a personas.
   *
   * La tabla `clientes` NO tiene columna `nombre` — el nombre vive en la tabla
   * `personas` como `razon_social` (para personas jurídicas) o la combinación
   * de `nombre` + `apellido` (para personas físicas). Este helper encapsula
   * ese join para evitar que los callers queryeen `.from('clientes').select('nombre')`
   * que devuelve error silencioso (null) y caía en el fallback genérico.
   *
   * Creado en ORR-62 Fase 7 como fix de un bug presente en Fases 2-5:
   * todas las notificaciones mostraban "Cliente sin nombre" porque la query
   * buggy fallaba silenciosamente.
   *
   * @returns el nombre computado, o 'Cliente sin nombre' si no se encuentra.
   */
  async obtenerNombreCliente(
    client: SupabaseClient<Database>,
    clienteId: string
  ): Promise<string> {
    const { data, error } = await client
      .from('clientes')
      .select('persona:personas(razon_social, nombre, apellido)')
      .eq('id', clienteId)
      .maybeSingle()

    if (error || !data) return 'Cliente sin nombre'

    type PersonaLite = {
      razon_social: string | null
      nombre: string | null
      apellido: string | null
    }
    const raw = (data as { persona?: PersonaLite | PersonaLite[] | null }).persona
    const persona = Array.isArray(raw) ? raw[0] : raw
    if (!persona) return 'Cliente sin nombre'

    if (persona.razon_social) return persona.razon_social

    const nombreCompleto = [persona.nombre, persona.apellido]
      .filter(Boolean)
      .join(' ')
      .trim()
    return nombreCompleto || 'Cliente sin nombre'
  },

  /**
   * ORR-613: emails de la lista cuyo usuario tiene `permiso` en la organización.
   * La RPC exige que el caller sea miembro activo de la organización. Ante cualquier error devuelve
   * [], porque un aviso con importes no puede salir sin el filtro.
   */
  async filtrarPorPermiso(
    client: SupabaseClient<Database>,
    organizacionId: string,
    emails: string[],
    permiso: keyof Permisos,
  ): Promise<string[]> {
    if (emails.length === 0) return []
    // La RPC no está en database.ts. El cast conserva `this` del cliente.
    const { data, error } = await (client as unknown as {
      rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>
    }).rpc('filtrar_destinatarios_por_permiso', {
      p_org: organizacionId,
      p_emails: emails,
      p_flag: permiso,
    })
    if (error) {
      console.error('[notificaciones] error filtrando destinatarios por permiso:', error)
      return []
    }
    return Array.isArray(data) ? (data as string[]) : []
  },

  /**
   * Obtiene los emails de los responsables de un cliente.
   * Si el cliente no tiene responsables asignados, cae al fallback:
   * todos los usuarios owner/admin activos de la org del cliente.
   *
   * Con `permiso` (ORR-613), primero van los responsables que lo tienen; si ninguno lo tiene, owner
   * y admin que lo tienen. La misma regla que notificar_clientes_morosos en SQL.
   *
   * @param excludeEmail — email a excluir (típicamente el actor)
   */
  async obtenerResponsablesCliente(
    client: SupabaseClient<Database>,
    clienteId: string,
    excludeEmail?: string,
    permiso?: keyof Permisos,
  ): Promise<string[]> {
    // ORR-69 security fix #2: tenant guard.
    // Antes de resolver responsables, verificamos que el actor pertenece
    // a la misma org que el cliente. Sin este gate, un user malicioso
    // podria pasar responsables:<uuid-cliente-otra-org> para enumerar
    // emails de responsables de otra organizacion.
    if (excludeEmail) {
      const { data: cliente, error: errCheck } = await client
        .from('clientes')
        .select('organizacion_id')
        .eq('id', clienteId)
        .maybeSingle()

      if (errCheck || !cliente?.organizacion_id) {
        console.warn(
          `[notificaciones] tenant guard: cliente "${clienteId}" no existe o sin org`,
        )
        return []
      }

      const { data: membership } = await client
        .from('usuario_organizaciones')
        .select('id, usuarios_brainhub!inner(email)')
        .eq('organizacion_id', cliente.organizacion_id)
        .eq('activo', true)
        .eq('usuarios_brainhub.email', excludeEmail)
        .maybeSingle()

      if (!membership) {
        console.warn(
          `[notificaciones] tenant guard: actor "${excludeEmail}" no es miembro de org del cliente "${clienteId}"`,
        )
        return []
      }
    }

    // 1. Intentar por cliente_responsables
    const { data: responsables, error: errResp } = await client
      .from('cliente_responsables')
      .select('usuarios_brainhub!inner(email, activo)')
      .eq('cliente_id', clienteId)

    if (errResp) {
      console.error('[notificaciones] error obteniendo responsables cliente:', errResp)
    }

    type Row = { usuarios_brainhub: { email: string | null; activo: boolean } | { email: string | null; activo: boolean }[] | null }
    const emails = new Set<string>()

    const rows = (responsables ?? []) as unknown as Row[]
    for (const row of rows) {
      const ub = row.usuarios_brainhub
      if (!ub) continue
      const items = Array.isArray(ub) ? ub : [ub]
      for (const item of items) {
        if (item?.activo && item.email && item.email !== excludeEmail) {
          emails.add(item.email)
        }
      }
    }

    // Si ya hay responsables y no hay permiso que filtrar, devolver
    if (emails.size > 0 && !permiso) return Array.from(emails)

    const { data: cliente, error: errCli } = await client
      .from('clientes')
      .select('organizacion_id')
      .eq('id', clienteId)
      .single()

    if (errCli || !cliente?.organizacion_id) {
      console.error('[notificaciones] cliente sin organizacion_id:', errCli)
      return []
    }
    const organizacionId = cliente.organizacion_id

    if (permiso) {
      const conPermiso = await notificacionesService.filtrarPorPermiso(
        client, organizacionId, Array.from(emails), permiso,
      )
      if (conPermiso.length > 0) return conPermiso
    }

    // 2. Fallback: todos los owner/admin de la org del cliente
    const admins = await notificacionesService.obtenerDestinatariosOrg(
      client, organizacionId, excludeEmail, ['owner', 'admin'],
    )
    return permiso
      ? notificacionesService.filtrarPorPermiso(client, organizacionId, admins, permiso)
      : admins
  },

  // ===========================================
  // Contadores y Resumen
  // ===========================================

  /**
   * Cuenta notificaciones no leidas
   */
  async contarNoLeidas(client: SupabaseClient<Database>, userEmail: string): Promise<number> {

    const { count, error } = await client
      .from('notificaciones')
      .select('*', { count: 'exact', head: true })
      .eq('user_email', userEmail)
      .eq('leida', false)

    if (error) throw new Error(`Error al contar notificaciones: ${error.message}`)
    return count ?? 0
  },

  /**
   * Obtiene resumen de notificaciones
   */
  async obtenerResumen(client: SupabaseClient<Database>, userEmail: string): Promise<ResumenNotificaciones> {

    const { data, error } = await client
      .from('notificaciones')
      .select('tipo, leida')
      .eq('user_email', userEmail)

    if (error) throw new Error(`Error al obtener resumen: ${error.message}`)

    const notificaciones = (data || []) as { tipo: string; leida: boolean }[]

    const porTipo: Record<TipoNotificacion, number> = {
      vencimiento: 0,
      tarea: 0,
      documento: 0,
      cliente: 0,
      sistema: 0,
      alerta: 0,
    }

    let noLeidas = 0

    notificaciones.forEach((n) => {
      porTipo[n.tipo as TipoNotificacion]++
      if (!n.leida) noLeidas++
    })

    return {
      total: notificaciones.length,
      noLeidas,
      porTipo,
    }
  },

  // NOTA (ORR-69): los 15 metodos notificar*() legacy fueron eliminados.
  // No tenian callers reales — solo eran invocados por el hook zombi
  // useNotificador() del archivo useNotificaciones.ts que tambien fue
  // eliminado. Usar notificacionesService.notificar() con origen trazable
  // para todas las notificaciones nuevas.

  // ===========================================
  // ORR-69: Metodo unificado notificar()
  // ===========================================
  /**
   * Metodo unificado para crear notificaciones. Convive con los metodos
   * notificar*() legacy — la migracion caller-por-caller es PR 2 de ORR-69.
   *
   * Resuelve destinatarios via destinatariosResolver (specs user:/org:/responsables:/origin),
   * excluye al actor automaticamente, llena `origen` en cada fila, y hace batch insert.
   * El trigger enviar_push_async respeta opt-out del usuario via preferencias_notificaciones.
   *
   * @example
   * await notificacionesService.notificar(client, {
   *   destinatarios: [`responsables:${clienteId}`],
   *   titulo: 'Nuevo comentario',
   *   mensaje: 'Juan comento en la tarea X',
   *   tipo: 'tarea',
   *   enlace: `/tareas/${tareaId}`,
   *   origen: {
   *     canal: 'web',
   *     actor_email: actorEmail,
   *     tabla_fuente: 'comentarios_tarea',
   *     registro_id: comentarioId,
   *   },
   * })
   */
  async notificar(
    client: SupabaseClient<Database>,
    input: {
      destinatarios: DestinatarioSpec | DestinatarioSpec[]
      titulo: string
      mensaje: string
      tipo: TipoNotificacion
      enlace?: string
      origen: NotificacionOrigen
      /**
       * ORR-613: si el aviso lleva importes, el flag que hace falta para verlos y la organización
       * donde se evalúa. Los destinatarios sin el flag se descartan.
       */
      permiso?: { flag: keyof Permisos; organizacionId: string }
    },
  ): Promise<void> {
    // ORR-69 security fix #3: validar que origen.actor_email coincide con
    // el usuario autenticado segun el JWT de Auth0. Previene spoofing de
    // autor en notificaciones. Usa la misma helper SQL SECURITY DEFINER
    // current_user_email() del commit 1, que resuelve auth.jwt() ->> 'sub'
    // contra usuarios_brainhub.auth0_user_id.
    //
    // Si el caller es un cron/sistema con canal='sistema' o 'cron', el JWT
    // puede no existir (ejecucion server-side via service_role). En ese
    // caso, current_user_email() devuelve null y skippeamos el check.
    const { data: authEmail } = await client.rpc('current_user_email')

    if (authEmail && input.origen.actor_email !== authEmail) {
      throw new Error(
        `notificar: origen.actor_email "${input.origen.actor_email}" no coincide con el usuario autenticado "${authEmail}"`,
      )
    }

    const resueltos = await destinatariosResolver.resolver(
      client,
      input.destinatarios,
      {
        actorEmail: input.origen.actor_email,
        origen: input.origen,
        permiso: input.permiso?.flag,
      },
      {
        obtenerDestinatariosOrg: notificacionesService.obtenerDestinatariosOrg,
        obtenerResponsablesCliente: notificacionesService.obtenerResponsablesCliente,
      },
    )
    const emails = input.permiso
      ? await notificacionesService.filtrarPorPermiso(
          client, input.permiso.organizacionId, resueltos, input.permiso.flag,
        )
      : resueltos

    if (emails.length === 0) {
      return
    }

    // Defense-in-depth: cap del batch a 500 destinatarios por llamada.
    // Si un caller resuelve `org:uuid` de una organizacion con 1000+
    // usuarios, el batch insert + el trigger enviar_push_async (N veces)
    // podria degradar el sistema. Este cap lo previene y loguea el corte
    // para que sea visible en monitoreo si alguna vez se alcanza.
    const BATCH_CAP = 500
    const trimmedEmails = emails.length > BATCH_CAP ? emails.slice(0, BATCH_CAP) : emails
    if (emails.length > BATCH_CAP) {
      console.warn(
        `[notificacionesService.notificar] batch cap alcanzado: ${emails.length} destinatarios -> limitado a ${BATCH_CAP}`,
      )
    }

    const rows = trimmedEmails.map((user_email) => ({
      user_email,
      titulo: input.titulo,
      mensaje: input.mensaje,
      tipo: input.tipo,
      enlace: input.enlace ?? '/notificaciones',
      // ORR-613: el trigger de la base evalúa el permiso en la organización de origen.
      origen: (input.permiso
        ? { ...input.origen, organizacion_id: input.permiso.organizacionId }
        : input.origen) as unknown as Database['public']['Tables']['notificaciones']['Insert']['origen'],
      leida: false,
    }))

    const { error } = await client.from('notificaciones').insert(rows)
    if (!error) return

    // La RLS de notificaciones rechaza el insert entero si un destinatario no es compañero activo del actor.
    // Con más de una fila, el insert se reintenta de a una fila y se descartan las rechazadas.
    if (error.code !== '42501' || rows.length === 1) {
      throw new Error(`Error al crear notificaciones: ${error.message}`)
    }
    const resultados = await Promise.all(rows.map((fila) => client.from('notificaciones').insert(fila)))
    const rechazadas = resultados.filter((r) => r.error).length
    if (rechazadas === rows.length) {
      throw new Error(`Error al crear notificaciones: ${error.message}`)
    }
    if (rechazadas > 0) {
      console.warn(`[notificacionesService.notificar] la RLS rechazó ${rechazadas} de ${rows.length} destinatarios`)
    }
  },

  // ===========================================
  // Novedades del sistema
  // ===========================================

  /**
   * Publica una novedad de tipo `sistema` a todos los usuarios activos de la organización.
   * El actor no la recibe. Las filas van en un solo insert: si falla, no le llega a nadie.
   * No verifica el rol: el caller exige que el actor sea admin.
   */
  async publicarNovedadSistema(
    client: SupabaseClient<Database>,
    input: { organizacionId: string; actorEmail: string; titulo: string; mensaje: string },
  ): Promise<void> {
    // notificaciones no tiene organizacion_id: la organización va en origen para poder listar por ella.
    const origen: NotificacionOrigen & { organizacion_id: string } = {
      canal: 'web',
      actor_email: input.actorEmail,
      tabla_fuente: NOVEDAD_SISTEMA_FUENTE,
      organizacion_id: input.organizacionId,
    }
    await notificacionesService.notificar(client, {
      destinatarios: `org:${input.organizacionId}`,
      titulo: input.titulo,
      mensaje: input.mensaje,
      tipo: 'sistema',
      origen,
    })
  },

  /** Últimas novedades de la organización que recibió el usuario, de la más nueva a la más vieja. */
  async listarNovedadesSistema(
    client: SupabaseClient<Database>,
    userEmail: string,
    organizacionId: string,
    limite = 5,
  ): Promise<Notificacion[]> {
    const { data, error } = await client
      .from('notificaciones')
      .select('*')
      .eq('user_email', userEmail)
      .eq('tipo', 'sistema')
      .eq('origen->>tabla_fuente', NOVEDAD_SISTEMA_FUENTE)
      .eq('origen->>organizacion_id', organizacionId)
      .order('created_at', { ascending: false })
      .limit(limite)

    if (error) throw new Error(`Error al listar novedades: ${error.message}`)
    return data as Notificacion[]
  },
}
